/**
 * The trade markers for a symbol — entries and exits — polled from the engine.
 *
 * Lifted out of ChartView so the one behaviour that is easy to get wrong — a
 * poll that keeps running after the user has switched the feature off — is
 * something a test can assert on directly, without mounting a chart.
 *
 * Polled rather than pushed: fills already arrive over the socket for P&L, but
 * the stored transaction is what carries the bot attribution, and that is
 * written on a separate path.
 *
 * When `enabled` is false this returns an empty array and never touches the
 * network. Empty is load-bearing beyond the drawing: the primitive hit-tests
 * whatever it was given, so no markers means no hit, which is what lets a click
 * where a marker used to be fall through to the diamond handler.
 *
 * The raw fills are what this holds in state, not the markers built from them.
 * Classifying a fill as an entry or an exit needs the CURRENT net position as
 * an anchor (see {@link buildTradeMarkers}), and that changes on every fill the
 * broker reports — far more often than the 30s poll. Keeping the fills and
 * deriving the markers means a position update relabels what is already drawn
 * immediately, without waiting for or forcing a refetch.
 *
 * `bars` is the one input this does not fetch — the chart already holds it, and
 * the max adverse excursion needs it. It stays OPTIONAL so the hook keeps its
 * one deliberate property: it can be rendered in a test with no chart, no
 * series and no store behind it, which is why the fetch was lifted in here in
 * the first place. No bars means no MAE and nothing else changes.
 */

import { useEffect, useMemo, useState } from 'react';
import { CONFIG } from '../config';
import { normalizeToBase } from '../utils/symbolUtils';
import {
  Bar,
  BotSignalRecord,
  buildTradeMarkers,
  FillRow,
  TradeMarker,
  signalLinkResolver,
} from '../utils/tradeMarkers';

/** Fills are written on a separate path from the socket, so this has to poll. */
export const TRADE_MARKER_POLL_MS = 30_000;

/** Stable default, so an absent `bars` is the same reference on every render. */
const NO_BARS: readonly Bar[] = Object.freeze([]);

/** Same, for the executions that resolve a bot fill back to its signal. */
const NO_EXECUTIONS: readonly BotSignalRecord[] = Object.freeze([]);

/**
 * A cheap fingerprint of the bars, standing in for their identity in the memo
 * below.
 *
 * `marketStore.updateOrAddCandle` rebuilds the candle array on every tick, so
 * the array's identity churns many times a second while the numbers MAE reads
 * barely move. Keying the rebuild on identity would re-walk every fill,
 * re-allocate every marker and push a fresh set into the chart primitive —
 * which forces a redraw — on each of those ticks, for a figure that did not
 * change.
 *
 * An excursion can only move when a bar's high or low moves, and the only bar
 * whose extremes can still move is the one still forming. So the live bar's
 * high and low are in the key, along with the count and the two end times that
 * catch a load, a prepend of older data, or a symbol switch. A tick that does
 * not stretch the forming bar cannot change any MAE, and correctly does not
 * change this string.
 *
 * The gap it accepts knowingly: a historical bar silently REVISED in place,
 * with the count and the live bar unchanged, is not noticed until the next bar
 * opens. That is a backfill correction to a bar the trade already lived
 * through, worth at most a tick of excursion on a figure the popup already
 * marks as approximate — and a symbol switch, which is the case where the whole
 * series is genuinely replaced, refetches the fills anyway.
 */
const barsKey = (bars: readonly Bar[]): string => {
  if (bars.length === 0) return '';
  const last = bars[bars.length - 1];
  return `${bars.length}:${bars[0].timestamp}:${last.timestamp}:${last.high}:${last.low}`;
};

/**
 * @param currentNetPos The broker's net position for `symbol` right now, or
 *   `null` while it is unknown. Resolved by the caller rather than read from
 *   brokerStore here: the caller is the one that knows which account and which
 *   contract months the chart is showing, and keeping the store out of this
 *   hook is what lets it be tested without one.
 *
 *   `null` yields no markers. The fetch still runs — the anchor usually
 *   resolves within a second of the socket connecting, and having the fills
 *   already in hand means the markers appear the moment it does.
 *
 * @param bars The chart's own candles, ascending, in milliseconds. Only the max
 *   adverse excursion reads them; leave them out and every marker is built
 *   exactly as before with `realized.mae` reporting unknown. Passed down rather
 *   than selected from the store here for the same reason `currentNetPos` is:
 *   a store read would tie this hook to a running chart.
 */
export function useTradeMarkerFeed(
  symbol: string,
  enabled: boolean,
  currentNetPos: number | null,
  bars: readonly Bar[] = NO_BARS,
  executions: readonly BotSignalRecord[] = NO_EXECUTIONS
): TradeMarker[] {
  const [fills, setFills] = useState<FillRow[]>([]);

  useEffect(() => {
    // Off means off: drop whatever is drawn and never start the timer. Leaving
    // a 30s poll running for a feature the user has hidden is pure waste.
    if (!enabled) {
      setFills([]);
      return;
    }

    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const base = CONFIG.getBackendUrl(window.location.hostname);
        const res = await fetch(
          `${base}/api/trade-transactions?limit=500&symbol=${encodeURIComponent(normalizeToBase(symbol))}`
        );
        if (!res.ok) return;
        const body = (await res.json()) as { transactions?: FillRow[] };
        if (cancelled) return;
        setFills(body.transactions ?? []);
      } catch {
        // engine down — leave whatever is already drawn rather than blanking it
      }
    };

    void load();
    const t = setInterval(load, TRADE_MARKER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [symbol, enabled]);

  // Re-derived on a position change as well as a fetch — the anchor moves the
  // entry/exit boundary, so the same fills can legitimately draw differently.
  // Guarded on `enabled` here too, so switching off blanks the chart in the
  // same render rather than one effect later.
  //
  // Keyed on the bars' FINGERPRINT rather than the array itself; the array is a
  // new one on every tick and almost none of those ticks can move an excursion.
  // See {@link barsKey} for what the key does and does not catch.
  const key = barsKey(bars);

  // Bot executions are the ONLY place the signal-to-fill link exists: a broker
  // fill comes back with no idea what caused it. Keyed on length and the newest
  // id rather than the array, which is replaced on every poll.
  const executionsKey = `${executions.length}:${executions[0]?.timestamp ?? ''}`;
  const resolveSignal = useMemo(
    () => signalLinkResolver(executions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executionsKey]
  );

  return useMemo(
    () => (enabled ? buildTradeMarkers(fills, currentNetPos, bars, resolveSignal) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, fills, currentNetPos, key, resolveSignal]
  );
}
